import { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Flex, VStack } from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { KText } from '@/ui-library/components/text';
import { PrepMarkdown } from './PrepMarkdown';
import { composeQuery, isTerminal, splitQuery } from './api';
import {
  useApplyPrepEdit,
  usePrepMessagePoll,
  usePrepMessages,
  useRetryPrepMessage,
  useSendPrepMessage,
} from './hooks';
import './prep.scss';

/* inline icons (these map to @kula-ai/icons in production) */
const Sparkle = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M12 3l1.9 4.6L18.5 9.5 14 11.4 12 16l-2-4.6L5.5 9.5 10 7.6z" />
    <circle cx="18.5" cy="5" r="1.2" fill="currentColor" />
    <circle cx="5.5" cy="17" r="1" fill="currentColor" />
  </svg>
);
const SendIcon = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="18"
    height="18"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    {...p}
  >
    <path d="M5 12h14M13 6l6 6-6 6" />
  </svg>
);
const Close = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);
const Chevron = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    {...p}
  >
    <path d="M9 6l6 6-6 6" />
  </svg>
);
const Check = (p: any) => (
  <svg
    viewBox="0 0 24 24"
    width="14"
    height="14"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    {...p}
  >
    <path d="M5 13l4 4L19 7" />
  </svg>
);

// The agent staged a concrete change → show it and let the interviewer confirm.
function ApplyCard({
  summary,
  state,
  onApply,
}: {
  summary: string;
  state: 'idle' | 'applying' | 'applied' | 'failed';
  onApply: () => void;
}) {
  return (
    <Box mt="12px" ml="34px" className="prep-reveal">
      <Box
        p="12px 14px"
        bg="primary-25"
        border="1px solid"
        borderColor="primary-100"
        borderRadius="12px"
      >
        <Flex align="center" gap="7px" mb="6px">
          <Box color="primary-500">
            <Sparkle width="13" height="13" />
          </Box>
          <KText
            size="xs-bold"
            color="primary-600"
            fontSize="10px"
            letterSpacing="0.07em"
            textTransform="uppercase"
            fontWeight="700"
          >
            Proposed change to your prep
          </KText>
        </Flex>
        <KText
          size="sm-regular"
          color="secondary-800"
          fontSize="13.5px"
          lineHeight="1.5"
          mb="11px"
        >
          {summary}
        </KText>
        {state === 'applied' ? (
          <Flex align="center" gap="6px" color="status-4">
            <Check />
            <KText
              size="sm-semi-bold"
              color="status-4"
              fontSize="13px"
              fontWeight="600"
            >
              Applied to prep
            </KText>
          </Flex>
        ) : (
          <>
            {state === 'failed' && (
              <KText
                size="xs-regular"
                color="status-1"
                fontSize="12.5px"
                mb="8px"
              >
                That didn’t go through — please try again.
              </KText>
            )}
            <Flex
              as="button"
              align="center"
              gap="8px"
              px="14px"
              py="8px"
              borderRadius="9px"
              color="base-white"
              bg="primary-500"
              opacity={state === 'applying' ? 0.7 : 1}
              cursor={state === 'applying' ? 'default' : 'pointer'}
              _hover={state === 'applying' ? {} : { bg: 'primary-600' }}
              onClick={state === 'applying' ? undefined : onApply}
            >
              {state === 'applying' && (
                <Box className="prep-spin prep-spin-sm" />
              )}
              <KText
                size="sm-semi-bold"
                color="base-white"
                fontSize="13px"
                fontWeight="600"
              >
                {state === 'applying'
                  ? 'Applying…'
                  : state === 'failed'
                    ? 'Try again'
                    : 'Apply to prep'}
              </KText>
            </Flex>
          </>
        )}
      </Box>
    </Box>
  );
}

// Starter prompts shown before the copilot has replied; once it has, the reply's own contextual
// follow-ups (message.followups) take their place.
const STARTERS = [
  'Co-create a question',
  'Time-box this round',
  'Strong vs weak answer',
];

const WELCOME =
  "Hi — I've put together your prep for this round. **Highlight anything** in the brief or résumé to dig in, or use a shortcut below to co-create a question or time-box the round. 👋";

interface Props {
  interviewId: number | string;
  pendingContext?: string | null;
  onContextConsumed?: () => void;
  onCollapse?: () => void;
}

const UserBubble = ({ query }: { query: string }) => {
  const { context, text } = splitQuery(query);
  return (
    <Flex direction="column" align="flex-end" gap="5px">
      {context && <Box className="prep-bubble-ctx">“{context}”</Box>}
      <Box className="prep-bubble-user">{text}</Box>
    </Flex>
  );
};

const AgentBubble = ({ children }: { children: React.ReactNode }) => (
  <Flex gap="10px" align="flex-start">
    <Flex
      flex="none"
      align="center"
      justify="center"
      w="24px"
      h="24px"
      mt="1px"
      borderRadius="7px"
      color="primary-500"
      bg="primary-50"
    >
      <Sparkle width="14" height="14" />
    </Flex>
    <Box flex={1} minW={0}>
      {children}
    </Box>
  </Flex>
);

export function PrepCopilot({
  interviewId,
  pendingContext,
  onContextConsumed,
  onCollapse,
}: Props) {
  const qc = useQueryClient();
  const { data: messages = [] } = usePrepMessages(interviewId);
  const sendMut = useSendPrepMessage(interviewId);
  const retryMut = useRetryPrepMessage(interviewId);
  const { apply } = useApplyPrepEdit(interviewId);

  const [input, setInput] = useState('');
  const [optimistic, setOptimistic] = useState<string[]>([]);
  const [applyingId, setApplyingId] = useState<number | null>(null);
  const [applyResult, setApplyResult] = useState<
    Record<number, 'applied' | 'failed'>
  >({});

  const onApply = async (id: number) => {
    setApplyingId(id);
    try {
      const ok = await apply(id);
      setApplyResult((prev) => ({ ...prev, [id]: ok ? 'applied' : 'failed' }));
    } catch {
      setApplyResult((prev) => ({ ...prev, [id]: 'failed' }));
    } finally {
      setApplyingId(null);
    }
  };
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // The single in-flight message (if any) drives the live progress label and re-syncs the list.
  const inflight = useMemo(
    () =>
      messages.find((m) => m.status === 'pending' || m.status === 'processing'),
    [messages]
  );
  const poll = usePrepMessagePoll(interviewId, inflight?.id);
  const progress = inflight ? poll.data?.progress || 'Thinking…' : null;

  // Pills: the most recent reply's own contextual follow-ups; before any reply, the generic starters.
  const pills = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const f = messages[i].followups;
      if (messages[i].status === 'completed' && f && f.length) return f;
    }
    return STARTERS;
  }, [messages]);

  // When the in-flight message reaches a terminal status, refresh the list so its response lands.
  useEffect(() => {
    if (poll.data && isTerminal(poll.data.status)) {
      qc.invalidateQueries({ queryKey: ['prep-messages', interviewId] });
    }
  }, [poll.data?.status, poll.data, qc, interviewId]);

  // Drop optimistic bubbles once the persisted list contains them.
  useEffect(() => {
    if (!optimistic.length) return;
    const existing = new Set(messages.map((m) => m.query));
    setOptimistic((prev) => prev.filter((q) => !existing.has(q)));
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    scrollRef.current?.scrollTo?.({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [messages, optimistic, progress]);

  useEffect(() => {
    if (pendingContext) inputRef.current?.focus();
  }, [pendingContext]);

  const send = (text: string, ctx?: string | null) => {
    const clean = text.trim();
    if (!clean && !ctx) return;
    const query = composeQuery(clean, ctx);
    setOptimistic((o) => [...o, query]);
    setInput('');
    // The textarea auto-grows on input; reset it to one row so the next message starts compact.
    if (inputRef.current) inputRef.current.style.height = 'auto';
    onContextConsumed?.();
    sendMut.mutate(query);
  };

  const isEmpty = messages.length === 0 && optimistic.length === 0;

  return (
    <Flex direction="column" h="100%" bg="secondary-25" minW={0}>
      {/* header */}
      <Flex
        align="center"
        gap="11px"
        px="18px"
        h="56px"
        flex="none"
        borderBottom="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        <Flex
          align="center"
          justify="center"
          w="32px"
          h="32px"
          borderRadius="9px"
          color="base-white"
          bgGradient="linear(135deg, primary-400, primary-600)"
          boxShadow="0 5px 14px -5px rgba(96,90,251,0.7)"
        >
          <Sparkle />
        </Flex>
        <Box flex={1} minW={0}>
          <KText size="sm-semi-bold" color="secondary-900" fontWeight="600">
            Prep Copilot
          </KText>
          <Flex align="center" gap="6px" mt="1px">
            <Box w="6px" h="6px" borderRadius="50%" bg="status-4" />
            <KText size="xs-regular" color="secondary-600" fontSize="11.5px">
              Grounded in this round
            </KText>
          </Flex>
        </Box>
        {onCollapse && (
          <Flex
            as="button"
            onClick={onCollapse}
            align="center"
            justify="center"
            w="30px"
            h="30px"
            borderRadius="8px"
            color="secondary-600"
            _hover={{ bg: 'secondary-50', color: 'secondary-900' }}
          >
            <Chevron />
          </Flex>
        )}
      </Flex>

      {/* messages */}
      <Box
        ref={scrollRef}
        className="prep-copilot prep-scroll"
        flex={1}
        overflowY="auto"
        px="18px"
        py="18px"
      >
        <VStack align="stretch" spacing="18px">
          {isEmpty && (
            <AgentBubble>
              <PrepMarkdown>{WELCOME}</PrepMarkdown>
            </AgentBubble>
          )}

          {messages.map((m) => (
            <Box key={m.id}>
              <UserBubble query={m.query} />
              {m.status === 'completed' && m.response && (
                <Box mt="18px">
                  <AgentBubble>
                    <PrepMarkdown>{m.response}</PrepMarkdown>
                  </AgentBubble>
                </Box>
              )}
              {m.proposes_prep_change && (
                <ApplyCard
                  summary={m.proposes_prep_change}
                  state={
                    m.prep_change_applied || applyResult[m.id] === 'applied'
                      ? 'applied'
                      : applyingId === m.id
                        ? 'applying'
                        : applyResult[m.id] === 'failed'
                          ? 'failed'
                          : 'idle'
                  }
                  onApply={() => onApply(m.id)}
                />
              )}
              {m.status === 'failed' && (
                <Box mt="18px">
                  <AgentBubble>
                    <Flex align="center" gap="10px">
                      <KText
                        size="sm-regular"
                        color="secondary-600"
                        fontSize="13.5px"
                      >
                        Something went wrong generating that reply.
                      </KText>
                      <Flex
                        as="button"
                        px="11px"
                        py="5px"
                        borderRadius="7px"
                        bg="primary-50"
                        color="primary-600"
                        _hover={{ bg: 'primary-100' }}
                        onClick={() =>
                          retryMut.mutate(m.id, {
                            onSuccess: () =>
                              qc.invalidateQueries({
                                queryKey: ['prep-messages', interviewId],
                              }),
                          })
                        }
                      >
                        <KText
                          size="xs-semi-bold"
                          color="primary-600"
                          fontSize="12px"
                          fontWeight="600"
                        >
                          Retry
                        </KText>
                      </Flex>
                    </Flex>
                  </AgentBubble>
                </Box>
              )}
            </Box>
          ))}

          {optimistic.map((q, i) => (
            <UserBubble key={`opt-${i}`} query={q} />
          ))}

          {progress && (
            <Flex gap="10px" align="center" pl="2px">
              <Box className="prep-spin" />
              <KText
                size="sm-regular"
                className="prep-progress-text"
                fontSize="13px"
              >
                {progress}
              </KText>
            </Flex>
          )}
        </VStack>
      </Box>

      {/* composer */}
      <Box
        flex="none"
        px="16px"
        pt="12px"
        pb="16px"
        borderTop="1px solid"
        borderColor="secondary-100"
        bg="base-white"
      >
        {pendingContext && (
          <Flex
            className="prep-reveal"
            align="flex-start"
            gap="10px"
            mb="9px"
            p="9px 12px"
            bg="secondary-25"
            border="1px solid"
            borderColor="secondary-100"
            borderLeft="2.5px solid"
            borderLeftColor="primary-400"
            borderRadius="9px"
          >
            <Box flex={1} minW={0}>
              <KText
                size="xs-bold"
                color="secondary-900"
                fontSize="10px"
                letterSpacing="0.06em"
                textTransform="uppercase"
                mb="3px"
              >
                Asking about
              </KText>
              <KText
                size="xs-regular"
                color="secondary-700"
                fontSize="12px"
                fontStyle="italic"
                noOfLines={2}
              >
                {pendingContext}
              </KText>
            </Box>
            <Flex
              as="button"
              onClick={onContextConsumed}
              color="secondary-500"
              _hover={{ color: 'secondary-900' }}
              pt="2px"
            >
              <Close />
            </Flex>
          </Flex>
        )}

        <Flex className="prep-chips" gap="8px" overflowX="auto" pb="11px">
          {pills.map((label, i) => (
            <Flex
              key={`${i}-${label}`}
              as="button"
              flex="none"
              align="center"
              gap="6px"
              px="12px"
              py="7px"
              borderRadius="20px"
              bg="primary-25"
              border="1px solid"
              borderColor="primary-200"
              transition="0.15s"
              _hover={{ borderColor: 'primary-400', bg: 'primary-50' }}
              // Keep any highlighted passage attached, so tapping a pill asks about the selection too.
              onClick={() => send(label, pendingContext)}
            >
              <Box flex="none" color="primary-400">
                <Sparkle width="12" height="12" />
              </Box>
              <KText
                size="xs-medium"
                color="primary-700"
                fontSize="12px"
                whiteSpace="nowrap"
                fontWeight="600"
              >
                {label}
              </KText>
            </Flex>
          ))}
        </Flex>

        <Flex
          align="flex-end"
          gap="9px"
          bg="base-white"
          border="1px solid"
          borderColor="secondary-200"
          borderRadius="13px"
          pl="14px"
          pr="7px"
          py="7px"
          transition="0.15s"
          _focusWithin={{
            borderColor: 'primary-400',
            boxShadow: '0 0 0 3px rgba(96,90,251,0.12)',
          }}
        >
          <Box
            as="textarea"
            ref={inputRef as any}
            flex={1}
            rows={1}
            value={input}
            placeholder={
              pendingContext
                ? 'What about this point?'
                : 'Ask the copilot, co-create a question, or time-box the round…'
            }
            onChange={(e: any) => {
              setInput(e.target.value);
              e.target.style.height = 'auto';
              e.target.style.height =
                Math.min(e.target.scrollHeight, 120) + 'px';
            }}
            onKeyDown={(e: any) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send(input, pendingContext);
              }
            }}
            sx={{
              border: 'none',
              outline: 'none',
              resize: 'none',
              bg: 'transparent',
              fontSize: '14px',
              lineHeight: '1.5',
              maxHeight: '120px',
              py: '5px',
              color: 'var(--kula-colors-secondary-900)',
              '::placeholder': { color: 'var(--kula-colors-secondary-500)' },
            }}
          />
          <Flex
            as="button"
            align="center"
            justify="center"
            flex="none"
            w="36px"
            h="36px"
            borderRadius="10px"
            color="base-white"
            bg="primary-500"
            opacity={!input.trim() && !pendingContext ? 0.45 : 1}
            cursor={!input.trim() && !pendingContext ? 'default' : 'pointer'}
            _hover={
              !input.trim() && !pendingContext ? {} : { bg: 'primary-600' }
            }
            boxShadow="0 6px 16px -7px rgba(96,90,251,0.7)"
            onClick={() => send(input, pendingContext)}
          >
            <SendIcon />
          </Flex>
        </Flex>
      </Box>
    </Flex>
  );
}

export default PrepCopilot;
